import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { authUtil } from 'src/utils';
import { useEffect, useRef } from 'react';

export type EditorClassification = {
  language?: string;
  category?: string;
  genres?: string[];
  tone?: string[];
  themes?: string[];
  normCreative?: boolean;
  narrativePOV?: string;
};

export type EditorFeedbackReport = {
  summary?: string;
  fixFirst?: { n: number; text: string }[];
  plotHoles?: {
    rank: number;
    title: string;
    severity: 'critical' | 'major' | 'minor';
    detail: string;
    quotes?: { ch: string | number; text: string }[];
  }[];
  characters?: {
    role: string;
    members: {
      name: string;
      cites?: string;
      traits?: string;
      motivation?: string;
      arc?: string;
    }[];
  }[];
  acts?: { name: string; chapters?: string; note?: string }[];
  unusedSetups?: { ch: string | number; title: string; note?: string }[];
  continuityNotes?: { ch: string | number; text: string }[];
};

export type EditorFeedbackStatus =
  'pending' | 'classifying' | 'analyzing' | 'verifying' | 'completed' | 'error';

export type EditorFeedback = {
  status: EditorFeedbackStatus;
  classification?: EditorClassification;
  teaserObservations?: string[];
  report?: EditorFeedbackReport;
  reportMarkdown?: string;
  creditsCharged?: number;
  reportsCharged?: number;
  progress?: { chaptersDone: number; totalChapters: number };
  model?: string;
  completedAt?: string;
  error?: string;
};

export type EditorFeedbackResponse = {
  editorFeedback: EditorFeedback | null;
  credits: {
    balance: number;
    resetDate?: string;
    monthlyCredits?: number;
    productInternalId?: string;
  };
};

type EditorFeedbackProps = {
  bookId?: string;
  contentId?: string;
};

const RUNNING_STATUSES: EditorFeedbackStatus[] = [
  'pending',
  'classifying',
  'analyzing',
  'verifying',
];

const baseUrl = (bookId: string, contentId: string) =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/books/${bookId}/content/${contentId}/editor-feedback`;

const useEditorFeedback = ({ bookId, contentId }: EditorFeedbackProps) => {
  const queryClient = useQueryClient();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const queryKey = ['editorFeedback', bookId, contentId];

  const editorFeedbackQuery = useQuery<EditorFeedbackResponse>(
    queryKey,
    async () => {
      const response = await fetch(
        baseUrl(bookId as string, contentId as string),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'br-token': await authUtil.getFreshIdToken(),
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch editor feedback');
      }
      return response.json();
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      staleTime: 1000 * 5,
      cacheTime: 1000 * 60 * 10,
      retry: 1,
      enabled: !!bookId && !!contentId,
    }
  );

  // Poll while a run is in progress.
  useEffect(() => {
    const status = editorFeedbackQuery.data?.editorFeedback?.status;
    const shouldPoll = !!status && RUNNING_STATUSES.includes(status);

    if (shouldPoll) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      pollingIntervalRef.current = setInterval(() => {
        editorFeedbackQuery.refetch();
      }, 4000);
    } else if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [
    editorFeedbackQuery.data?.editorFeedback?.status,
    editorFeedbackQuery.refetch,
  ]);

  const startMutation = useMutation(
    async (body?: {
      classification?: EditorClassification;
      reportLanguage?: string;
    }) => {
      const response = await fetch(
        baseUrl(bookId as string, contentId as string),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': await authUtil.getFreshIdToken(),
          },
          body: JSON.stringify(body || {}),
        }
      );
      const data = await response.json().catch(() => ({}));
      return { ok: response.ok, status: response.status, data };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  const classifyMutation = useMutation(
    async () => {
      const response = await fetch(
        `${baseUrl(bookId as string, contentId as string)}/classify`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': await authUtil.getFreshIdToken(),
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to detect editor voice');
      }
      return response.json() as Promise<{
        classification: EditorClassification;
        observations?: string[];
      }>;
    },
    {
      // Refetch so freshly-detected persona + teaser observations render.
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  const saveClassificationMutation = useMutation(
    async (classification: EditorClassification) => {
      const response = await fetch(
        `${baseUrl(bookId as string, contentId as string)}/classification`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'br-token': await authUtil.getFreshIdToken(),
          },
          body: JSON.stringify({ classification }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to save editor voice');
      }
      return response.json();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  return {
    editorFeedbackQuery,
    // Returns {ok, status, data}; caller handles 402/409.
    startEditorFeedback: startMutation.mutateAsync,
    isStarting: startMutation.isLoading,
    classify: async () => {
      try {
        return await classifyMutation.mutateAsync();
      } catch (error) {
        toast.error('Failed to detect editor voice');
        console.error('Error detecting editor voice:', error);
        return undefined;
      }
    },
    isClassifying: classifyMutation.isLoading,
    saveClassification: async (classification: EditorClassification) => {
      try {
        const result =
          await saveClassificationMutation.mutateAsync(classification);
        return result;
      } catch (error) {
        toast.error('Failed to save editor voice');
        console.error('Error saving editor voice:', error);
        return undefined;
      }
    },
    isSaving: saveClassificationMutation.isLoading,
  };
};

export default useEditorFeedback;
